import ko from 'knockout';
import $ from 'jquery';
import Inputmask from 'inputmask';
import moment from 'moment';
import numeral from 'numeral';
import 'moment-duration-format';
import flatpickr from 'flatpickr';
import noUiSlider from 'nouislider';
import Choices, { Options as ChoicesOptions } from 'choices.js';
import 'select2';

import { CustomError } from './fetch';
import { OptionData } from 'select2';
import {getCookie} from "./custom/cookieHelpers";

const languageCookie = getCookie('.AspNetCore.Culture');
const isEnglish = languageCookie && languageCookie.indexOf('en') > -1;

ko.bindingHandlers.checkedRadioToBool = {
    init: function (element: any, valueAccessor: any) {
        const observable = valueAccessor();
        const interceptor = ko.computed({
            read: function () {
                return observable().toString();
            },
            write: function (newValue) {
                if(newValue === "true" || newValue === "false") {
                    observable(newValue === "true");
                } else if(!isNaN(Number(newValue))) {
                    observable(Number(newValue));
                } else {
                    observable(newValue);
                }
            },
            owner: this
        });
        ko.applyBindingsToNode(element, { checked: interceptor });
    }
};

const defaultSelectPickerOptions: ChoicesOptions = {
    itemSelectText: ''
};

const initSelectPicker = function (element: any, allBindingsAccessor: any) {
    const allOptions: ChoicesOptions = {
        ...defaultSelectPickerOptions,
        ...allBindingsAccessor().initPickerOptions
    };
    let choices = new Choices(element, allOptions);

    const $el = $(element);
    $el.data('choices', choices);
};

ko.bindingHandlers.selectPicker = {
    init: function (element: any, valueAccessor: any, allBindingsAccessor: any) {
        initSelectPicker(element, allBindingsAccessor);

        // Do not add the event handlers in the init function
        // The init function is also used to reset the choicesjs if the dataset changes
        // And then, the eventhandlers are registered multiple times, which should not be done
        ko.utils.registerEventHandler(element, 'addItem', () => {
            const choices = $(element).data('choices');
            const isMultiple = element.multiple;

            if (isMultiple) {
                let values = <any[]>choices.getValue()
                let val = values.map((v) => {
                    return { label: v.label, value: v.value }
                });
                choices.hideDropdown();
                valueAccessor()(val);
            } else {
                let values = <any>choices.getValue();

                let val = {
                    label: values.label,
                    value: values.value
                };

                choices.hideDropdown();
                valueAccessor()(val);
            }
        });

        ko.utils.registerEventHandler(element, 'removeItem', (event: any) => {
            const isMultiple = element.multiple;
            if (isMultiple) {
                const newValues = valueAccessor()()
                    ?.filter((value: any) => value.value !== event.detail.value)
                    ?? [];
                valueAccessor()(newValues);
            } else {
                if (valueAccessor()()?.value === event.detail.value) {
                    valueAccessor()(null);
                }
            }
        });

        ko.utils.domNodeDisposal.addDisposeCallback(element, () => {
            const choices = $(element).data('choices');
            choices.destroy();
        });
    },
    update: function (element: any, valueAccessor: any, allBindingsAccessor: any) {
        let options = allBindingsAccessor().selectPickerOptions;
        const $el = $(element);
        let choices = $el.data('choices');
        const isMultiple = element.multiple;

        const currentChoices = choices.store.getChoices();
        const givenChoices = ko.utils.unwrapObservable(options.dataset);
        const choicesChanged = currentChoices.length !== givenChoices.length
            || currentChoices.some((c: any) => givenChoices.find((cc: any) =>
                cc[options.labelField] == c.label && cc[options.valueField] == c.value) == null);

        if (choicesChanged) {
            choices.destroy();
            initSelectPicker(element, allBindingsAccessor);
            choices = $el.data('choices');
        }

        const choicesValue = choices.getValue();
        const currentValue = ko.utils.unwrapObservable(valueAccessor());

        const valueChanged = isMultiple
            ? choicesValue.length !== currentValue.length
                || choicesValue.some((v: any) => currentValue.find((vv: any) => vv.value === v.value) == null)
            : choicesValue?.value !== currentValue?.value;

        if (valueChanged || choicesChanged) {
            const dataset = givenChoices.map((ds: any) => {
                return {
                    selected: isMultiple
                        ? valueAccessor()()?.find((t: any) => t.value === ds[options.valueField]) != null
                        : valueAccessor()()?.value === ds[options.valueField],
                    ...ds
                };
            });

            choices.setChoices(dataset, options.valueField, options.labelField, true);
        }

        ko.utils.domNodeDisposal.addDisposeCallback(element, () => {
            choices.destroy();
        });

        requestAnimationFrame(() => {
            let choicesContainer = element.closest('.choices');

            // Retry until the wrapper exists
            const waitForWrap = () => {
                choicesContainer = element.closest('.choices');

                if (!choicesContainer) {
                    requestAnimationFrame(waitForWrap);
                    return;
                }

                const applyInvalid = () => {
                    if(valueAccessor().isModified == null || valueAccessor().isValid == null) {
                        return;
                    }
                    
                    const showInvalid = (valueAccessor().isModified() && !valueAccessor().isValid());
                    if (showInvalid) {
                        choicesContainer.classList.add("is-invalid");
                    } else {
                        choicesContainer.classList.remove("is-invalid");
                    }
                };

                valueAccessor().subscribe(applyInvalid);
                applyInvalid();
            };

            waitForWrap();
        });
    }
};

ko.bindingHandlers.select2 = {
    init: function (el, valueAccessor, allBindingsAccessor, _viewModel) {
        ko.utils.domNodeDisposal.addDisposeCallback(el, function () {
            $(el).select2('destroy');
        });

        var allBindings = allBindingsAccessor();
        var select2 = ko.utils.unwrapObservable(allBindings.select2Options);

        $(el).select2(select2);

        $(el).on('select2:select', item => {
            const newValue = [...valueAccessor()(), { ...item.params.data }];

            valueAccessor()(newValue);
        });

        $(el).on('select2:unselect', event => {
            const item: any = event.params.data;
            const optionsValue = allBindings.optionsValue;
            const newValue = [...valueAccessor()()].filter(i => i[optionsValue] != item[optionsValue]);

            valueAccessor()(newValue);
        });
    }
};


const defaultDateTimePickerOptions = {
    dateFormat: 'd-m-Y',
    parseFormat: 'DD-MM-YYYY',
    enableTime: false,
    allowInput: true,
    onReady: (_1: any, _2: any, instance: any) => {
        const $el = $(instance.calendarContainer);
        if ($el.find('flatpickr-clear').length < 1) {
            $el.append('<div class="flatpickr-clear clickable">Reset</div>');
            $el.find('.flatpickr-clear').on('click', () => {
                instance.clear();
                instance.close();
            });
        }
    }
};

ko.bindingHandlers.dateTimePicker = {
    init: (element: any, valueAccessor: any, allBindingsAccessor: any) => {
        const options = {
            ...defaultDateTimePickerOptions,
            ...allBindingsAccessor().dateTimePickerOptions
        };

        const picker = flatpickr(element, options);
        const $el = $(element);
        $el.data('dtp', picker);

        ko.utils.registerEventHandler(element, 'change', () => {
            let value: moment.Moment | undefined = moment(element.value, 'DD-MM-YYYY');
            if (!value.isValid()) {
                value = undefined;
            }
            valueAccessor()(value);
        });
    },
    update: (element: any, valueAccessor: any) => {
        const picker = $(element).data('dtp');
        const value = ko.unwrap(valueAccessor()) || '';
        const momentValue = moment(value);
        if (momentValue.isValid()) {
            picker.setDate(momentValue.format('DD-MM-YYYY'));
        } else {
            picker.clear();
        }
    }
};

(<any>ko).validation.makeBindingHandlerValidatable('dateTimePicker');

ko.bindingHandlers.dateInputMask = {
    init: function (element: any, valueAccessor: any) {
        $(element).on('focusout change', function () {
            if (element.inputmask.isComplete()) {
                let time = moment(element.value, 'DD-MM-YYYY HH:mm');
                valueAccessor()(time);
            } else {
                valueAccessor()(null);
            }
        });

        Inputmask('dd-mm-yyyy', {
            placeholder: 'dd/mm/jjjj',
            clearMaskOnLostFocus: false
        }).mask(element);
    },
    update: function (element: any, valueAccessor: any) {
        let value = ko.utils.unwrapObservable(valueAccessor());
        let text = moment(value).format('DD-MM-YYYY HH:mm');
        element.value = text;
    }
};

(<any>ko).validation.makeBindingHandlerValidatable('dateInputMask');

ko.bindingHandlers.dateTimeInputMask = {
    init: function (element: any, valueAccessor: any) {
        $(element).on('focusout change', function () {
            if (element.inputmask.isComplete()) {
                let time = moment(element.value, 'DD-MM-YYYY HH:mm');
                valueAccessor()(time);
            } else {
                valueAccessor()(null);
            }
        });

        Inputmask('datetime', {
            placeholder: 'dd/mm/jjjj uu:mm',
            clearMaskOnLostFocus: false
        }).mask(element);
    },
    update: function (element: any, valueAccessor: any) {
        let value = ko.utils.unwrapObservable(valueAccessor());
        let text = moment(value).format('DD-MM-YYYY HH:mm');
        element.value = text;
    }
};

(<any>ko).validation.makeBindingHandlerValidatable('dateTimeInputMask');

ko.bindingHandlers.timeInputMaskHoursMinutes = {
    init: (element: any, valueAccessor: any) => {
        $(element).on('focusout change', () => {
            if (element.inputmask.isComplete()) {
                valueAccessor()(element.value);
            } else {
                valueAccessor()(undefined);
            }
        });

        Inputmask('hh:mm', {
            placeholder: 'hh:mm',
            clearMaskOnLostFocus: false
        }).mask(element);
    },
    update: (element: any, valueAccessor: any) => {
        const value = ko.utils.unwrapObservable(valueAccessor());
        const text = value
            ? moment.duration(value).format("HH:mm", { trim: false })
            : element.value;
        element.value = text;
    }
};

(<any>ko).validation.makeBindingHandlerValidatable('timeInputMaskHoursMinutes');

ko.bindingHandlers.timeMsInputMaskHoursMinutes = {
    init: (element: any, valueAccessor: any) => {
        $(element).on('focusout change', () => {
            if (element.inputmask.isComplete()) {
                let duration = moment.duration(element.value);
                valueAccessor()(duration.asMilliseconds());
            } else {
                valueAccessor()(null);
            }
        });

        Inputmask('hh:mm', {
            placeholder: "hh:mm",
            clearMaskOnLostFocus: false
        }).mask(element);
    },
    update: (element: any, valueAccessor: any) => {
        let value = ko.utils.unwrapObservable(valueAccessor());
        let text = value != null
            ? moment.duration(value).format("HH:mm", { trim: false })
            : element.value;
        element.value = text;
    }
};

(<any>ko).validation.makeBindingHandlerValidatable('timeMsInputMaskHoursMinutes');

ko.bindingHandlers.timeMsInputMask = {
    init: (element: any, valueAccessor: any) => {
        $(element).on('focusout change', () => {
            if (element.inputmask.isComplete()) {
                let duration = moment.duration(element.value);
                valueAccessor()(duration.asMilliseconds());
            } else {
                valueAccessor()(null);
            }
        });

        Inputmask('hh:mm:ss', {
            placeholder: "uu:mm'ss",
            clearMaskOnLostFocus: false
        }).mask(element);
    },
    update: (element: any, valueAccessor: any) => {
        let value = ko.utils.unwrapObservable(valueAccessor());
        let text = value != null
            ? moment.duration(value).format("HH:mm'ss", { trim: false })
            : element.value;
        element.value = text;
    }
};

(<any>ko).validation.makeBindingHandlerValidatable('timeMsInputMask');

ko.bindingHandlers.noUiSlider = {
    init: (element: any, valueAccessor: any, allBindingsAccessor: KnockoutAllBindingsAccessor) => {
        const values = valueAccessor().map((v: any) => ko.utils.unwrapObservable(v));
        const noUiSliderOptions = allBindingsAccessor.get('noUiSliderOptions');
        const options = {
            ...noUiSliderOptions,
            start: values
        };

        noUiSlider.create(element, options);
        element.noUiSlider.on('update', (_: any[], handle: number, unencoded: number[]) => {
            const newValue = unencoded[handle];
            valueAccessor()[handle](newValue);
        });
    }
};

(<any>ko).validation.makeBindingHandlerValidatable('noUiSlider');

ko.bindingHandlers.decimalInputMask = {
    init: (element: any, valueAccessor: any) => {
        $(element).on('focusout change', () => {
            if (element.value.trim() === '') {
                // Explicitly "not filled in" - distinct from an incomplete/invalid value below.
                valueAccessor()(null);
            } else if (element.inputmask.isComplete()) {
                let number = numeral(element.value).value();
                valueAccessor()(number);
            } else {
                valueAccessor()(undefined);
            }
        });

        Inputmask('decimal', {
            rightAlign: false,
            autoGroup: true,
            digits: 2,
            radixPoint: isEnglish ? '.' : ',',
            groupSeparator: isEnglish ? ',' : '.'
        }).mask(element);
    },
    update: function (element, valueAccessor) {
        let value = ko.utils.unwrapObservable(valueAccessor());
        // Covers both null (not filled in) and undefined (rejected/incomplete input) - write
        // an explicit empty string rather than relying on the DOM to coerce either to "".
        let text = value == null
            ? ''
            : numeral(value).format('0,0.00');

        element.value = text;
    }
};


(<any>ko).validation.makeBindingHandlerValidatable('decimalInputMask');

(ko.extenders as any).range = (target: KnockoutObservable<any>, range: { min: number, max: number }) => {
    //create a writeable computed observable to intercept writes to our observable
    let result = ko.pureComputed({
        read: target, //always return the original observables value
        write: (newValue: any) => {
            let current = target();
            let newValueAsNum = isNaN(newValue) ? 0 : parseInt(`${newValue}`, 10);
            let outOfRange = false;

            if (newValueAsNum < range.min) {
                newValue = `${range.min}`;
                outOfRange = true;
            }

            if (newValueAsNum > range.max) {
                newValue = `${range.max}`;
                outOfRange = true;
            }

            // only write if it changed
            if (newValue !== current) {
                target(newValue);
            } else if (outOfRange) {
                // or if value was out of range (so input field is triggered to change)
                target.notifySubscribers(newValue);
            }
        }
    }).extend({ notify: 'always' });

    // initialize with current value to make sure it is rounded appropriately
    result(target());

    // return the new computed observable
    return result;
};

(ko.extenders as any).errorHandler = (
    target: KnockoutObservable<any>,
    options: {
        source: KnockoutObservableArray<CustomError>, id: string
    }) => {
    target.errors = ko.observableArray<string>([]);

    // If target already has an error observable,
    // use this to indicate if that error is external
    target.isOverwritten = ko.observable(false);

    // If a target already has an error observable, this will clash
    // so, use the existing error observable as error, instead of our own pureComputed
    // This is the case if this is used along side with knockout validation
    if (target.error == null) {
        target.error = ko.pureComputed<string>(() => {
            return target.errors().length > 0
                ? target.errors()[0]
                : '';
        });
    } else {
        // If the error changes, it is changes by external source
        // So keep track that it is overwritten
        let settingServerError = false;
        target.error.subscribe((error) => {
            if (!settingServerError) {
                target.isOverwritten(!!error);
            }
        });

        // If our error list changes,
        // we want to show that error, so set error to our error
        // and set isOverwritten to false in order to show it
        target.errors.subscribe((errors: any) => {
            if (errors.length > 0) {
                settingServerError = true;
                target.error(errors[0]);
                settingServerError = false;
                target.isOverwritten(false);
            }
        });
    }

    // Only show our error if it is not overwritten
    // This is because knockout validation will insert its own message
    // and we do not want to show the same message twice
    target.hasError = ko.pureComputed<boolean>(() => {
        return !target.isOverwritten() && target.errors().length > 0;
    });

    const validate = (errors: CustomError[]) => {
        if (!errors || errors.length === 0 || !options.id) {
            target.errors([]);
        } else {
            const targetErrors = errors
                .filter(e => e.key === options.id)
                .map(e => e.message);
            target.errors(targetErrors);
        }
    };

    validate(options.source());

    options.source.subscribe(validate);

    return target;
};
